/**
 * Client picker — a remote-search combobox over the clients directory.
 *
 * It was a `Select` that preloaded the newest 200 clients, with a footer
 * admitting when the list was clipped. That is the right control for a
 * handful of tenants and the wrong one for the flagship's tens of
 * thousands: a client outside the window could not be chosen at all, on
 * any of the five surfaces that mount this.
 *
 * So the directory is never downloaded. The operator types, the server
 * searches (name + slug, ilike), and at most `SEARCH_LIMIT` rows come
 * back. Three properties make that safe:
 *
 * - **Selection is an immutable client id.** The label is a lookup, so a
 *   renamed client cannot silently change what a form points at.
 * - **The selected client is resolved BY ID**, on its own query, so its
 *   name stays on the trigger while a new search is in flight, when it
 *   falls outside the current results, and on a cold deep link where the
 *   browser holds an id and nothing else.
 * - **Stale replies cannot paint.** Every parameter is in the query key,
 *   so a slow answer for an earlier keystroke resolves into the cache
 *   entry it belongs to. No AbortController, no request counter.
 */
import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '../api/query-keys'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  selectTriggerVariants,
} from '@speaking-coach/ui'
import {
  Listbox,
  ListboxMessage,
  ListboxOption,
  ListboxSearch,
  useListboxId,
} from '../components/ui/listbox'
import { cn } from '../components/ui/cn'
import { adminClientsApi, messageForClientError } from './api'
import { clientByIdParams, useClient } from './use-client'
import type { ClientRow, ListClientsParams } from './types'

/** Long enough that typing a name does not fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 250

/**
 * One screenful. The point is NOT to show every match — it is to show
 * enough that the operator either sees the one they want or narrows
 * further, which the footer tells them to do.
 */
const SEARCH_LIMIT = 20

/**
 * The clear row is addressed by value like any other option, so it needs
 * one that cannot collide with a client id.
 */
const CLEAR_VALUE = '__all_clients__'

interface ClientPickerProps {
  id: string
  value: string
  onChange: (clientId: string) => void
  /** Label text. The caller owns the `<label>`; this renders the control. */
  label: string
  /**
   * Text for the empty option. Supplying it makes the picker a FILTER
   * ("All clients"); omitting it makes selection required.
   */
  emptyLabel?: string
  /**
   * Marks the field as required for the operator. Validation itself is
   * the form's zod schema — every form here carries `noValidate`, so
   * native constraint validation was never what enforced this.
   */
  required?: boolean
  disabled?: boolean
  ariaInvalid?: boolean
  ariaDescribedby?: string
  /** Default in forms; `sm` only when the picker has a compact utility role. */
  size?: 'default' | 'sm'
  /** Forms may need disambiguation; filter rails can stay scan-friendly. */
  showDetails?: boolean
  /**
   * Offer only clients with at least one live, unended cohort. This is a
   * window-state filter, not proof that every enrollment default is complete.
   *
   * This is a SERVER filter (`hasJoinableCohort`), not a predicate over
   * the fetched rows: filtering in the browser can only ever narrow one
   * page, so a client with a cohort sitting past the search limit stayed
   * invisible. The CURRENT selection is exempt — it is resolved by id on
   * a separate, unfiltered query — because a pre-filled value that
   * rendered as unchosen would misreport the form's state.
   */
  requireJoinableCohort?: boolean
}

function describe(client: ClientRow, showDetails: boolean): string {
  if (!showDetails) return client.name
  return `${client.name}${client.isInternal ? ' (internal)' : ''} · ${client.slug}`
}

export function ClientPicker({
  id,
  value,
  onChange,
  label,
  emptyLabel,
  required = false,
  disabled = false,
  ariaInvalid,
  ariaDescribedby,
  size = 'default',
  showDetails = true,
  requireJoinableCohort = false,
}: ClientPickerProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const listId = useListboxId()

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  // Alphabetical rather than the endpoint's newest-first default: a
  // person hunting a name scans an alphabet, not a timeline.
  const searchParams: ListClientsParams = {
    search: search || undefined,
    hasJoinableCohort: requireJoinableCohort ? true : undefined,
    sortField: 'name',
    sortDirection: 'asc',
    limit: SEARCH_LIMIT,
  }
  const results = useQuery({
    queryKey: queryKeys.clientList(searchParams),
    queryFn: () => adminClientsApi.list(searchParams),
    // Nothing is fetched until the popover opens — five pickers across
    // the app must not each cost a request on every page load.
    enabled: open,
    // Each keystroke mints a new key; without this the list unmounts into
    // "Searching…" and flashes on every character.
    placeholderData: keepPreviousData,
  })

  const selected = useClient(value)
  const rows = results.data?.clients ?? []
  const total = results.data?.total ?? 0

  // Enter acts on the highlighted row, and the rows arrive from the server
  // AFTER the list first renders — so where the cursor sits when results
  // land is the whole ballgame. It gets stated here, once: the first match,
  // or the clear row when there are no matches to point at.
  //
  // This used to be a fight rather than a rule. cmdk re-homed the cursor
  // from its own item bookkeeping whenever the highlighted row vanished
  // (which every search does), landing on the first row in DOM order and
  // overwriting anything set from out here — so Enter chose the wrong row,
  // and in a filter it cleared the filter. Owning the listbox means the
  // rule below is simply what happens.
  //
  // Keyed on the first match's ID, not on `rows`: that array is a new
  // identity every render, and re-running this while the operator is
  // arrowing down the list would yank them back to the top.
  const [active, setActive] = useState('')
  const firstMatchId = rows[0]?.clientId ?? ''
  const clearable = emptyLabel !== undefined
  useEffect(() => {
    setActive(firstMatchId || (clearable ? CLEAR_VALUE : ''))
  }, [firstMatchId, clearable])

  function choose(client: ClientRow | null): void {
    // Seed the by-id entry the label reads from, so choosing a client the
    // search already returned does not cost a second round trip to learn
    // its name.
    if (client !== null) {
      queryClient.setQueryData(queryKeys.clientList(clientByIdParams(client.clientId)), {
        clients: [client],
        total: 1,
      })
    }
    onChange(client?.clientId ?? '')
    setOpen(false)
    setQuery('')
    setSearch('')
  }

  // What the arrow keys walk and what Enter commits, in ONE order — the
  // same order the rows render in, so the cursor never disagrees with the
  // list. The clear row is last here for the same reason it is last there.
  const optionIds = [...rows.map((row) => row.clientId), ...(clearable ? [CLEAR_VALUE] : [])]

  function selectByValue(candidate: string): void {
    if (candidate === CLEAR_VALUE) {
      choose(null)
      return
    }
    const client = rows.find((row) => row.clientId === candidate)
    if (client) choose(client)
  }

  const placeholder = emptyLabel ?? '— select a client —'
  const triggerLabel =
    value === ''
      ? placeholder
      : selected.data
        ? describe(selected.data, showDetails)
        : selected.isPending
          ? 'Loading client…'
          : // Resolved to nothing, or the lookup failed: say so rather
            // than showing a bare id or an empty box that reads as unset.
            'Client unavailable'

  // A filter that is SET needs a one-click way out. Clearing it through
  // the list means open, find "All clients", click — three actions to undo
  // one, on the control an operator toggles most often. Only in filter
  // mode: a required form field has nothing to clear TO.
  //
  // A sibling of the trigger, absolutely positioned over its right edge,
  // never a child — a button inside a button is invalid HTML, and Radix
  // renders the trigger as one. Being a sibling also puts it in the tab
  // order on its own, so the keyboard gets the shortcut too.
  const showClear = clearable && value !== '' && !disabled

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative flex w-full items-center">
      <PopoverTrigger
        id={id}
        // The control opens a list of options, so it is a combobox even
        // though the layer it opens is a Radix popover — Radix supplies
        // `aria-expanded` and `aria-haspopup`, both valid on this role.
        role="combobox"
        disabled={disabled}
        aria-label={label}
        aria-required={required || undefined}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedby}
        // `role="combobox"` promises the ARIA combobox keyboard contract,
        // and ArrowDown/ArrowUp opening the list is part of it. Radix's
        // Popover trigger only handles Enter and Space, and the Select this
        // replaced DID open on the arrows — so without this the swap is a
        // keyboard regression on all five call sites at once.
        //
        // The old closed-state typeahead (jump to a client by typing its
        // first letters against a shut Select) is deliberately NOT restored:
        // the open list's search box is the better version of it, and it
        // reaches the whole directory rather than the loaded window.
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          selectTriggerVariants({ size }),
          // Room for the chevron, plus the clear button when it is there.
          // Both are positioned against the WRAPPER rather than living in
          // this button, so the padding is the only thing keeping the label
          // from sliding under them.
          showClear ? 'pr-14' : 'pr-9',
        )}
      >
        <span className={value === '' ? 'text-text-faint' : undefined}>
          {triggerLabel}
        </span>
      </PopoverTrigger>
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          // Names what it clears, because a rail can hold several of these
          // and "Clear" alone would read identically on all of them.
          aria-label={`Clear ${label.toLowerCase()} filter`}
          title={`Clear ${label.toLowerCase()} filter`}
          onClick={() => choose(null)}
          className="absolute right-7 size-6 text-text-secondary hover:text-text-primary"
        >
          <Icon name="clear" size={14} />
        </Button>
      )}
      {/* Outside the trigger so the clear button can sit BETWEEN the label
          and it — the conventional order, and impossible while the chevron
          was a flex child being pushed inward by the trigger's own right
          padding. `pointer-events-none` keeps it decorative: a click on the
          chevron still opens the list, because it lands on the trigger
          underneath. */}
      <Icon
        name="choose"
        className="pointer-events-none absolute right-3 opacity-60"
      />
      </div>
      <PopoverContent
        // Match the trigger's width so the list lines up under the field
        // instead of floating at its content width.
        className="w-(--anchor-width) p-0"
      >
        <div className="border-b border-border-default">
          <ListboxSearch
            listId={listId}
            label={`Search ${label.toLowerCase()}`}
            placeholder="Search name or slug..."
            value={query}
            onValueChange={setQuery}
            activeId={active}
            optionIds={optionIds}
            onActiveIdChange={setActive}
            onSelect={selectByValue}
            onDismiss={() => setOpen(false)}
          />
        </div>
        <Listbox id={listId} label={label}>
          {results.isError && (
            <li role="presentation" className="px-3 py-4 text-center">
              <p role="alert" className="text-body text-danger-text">
                Could not load clients: {messageForClientError(results.error)}
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => void results.refetch()}
              >
                Try again
              </Button>
            </li>
          )}

          {!results.isError && results.isPending && (
            <ListboxMessage>Searching clients…</ListboxMessage>
          )}

          {!results.isError && results.isSuccess && rows.length === 0 && (
            <ListboxMessage>
              {search !== ''
                ? `No clients match “${search}”.`
                : requireJoinableCohort
                  ? // NOT "no clients yet": the filter is on, so this is a
                    // system that may be full of clients whose cohorts
                    // have all ended. Sending the operator to create
                    // another client would be the wrong repair.
                    'No clients have a cohort a wave can join. Extend a cohort’s access end, or create one.'
                  : 'No clients yet.'}
            </ListboxMessage>
          )}

          {rows.map((client) => (
            <ListboxOption
              key={client.clientId}
              listId={listId}
              value={client.clientId}
              active={active === client.clientId}
              onSelect={() => choose(client)}
              onHover={() => setActive(client.clientId)}
            >
              <Icon
                name="selected"
                className={client.clientId === value ? 'opacity-100' : 'opacity-0'}
              />
              {describe(client, showDetails)}
            </ListboxOption>
          ))}

          {/* Honest about the cap, the way the old footer was honest
              about the 200-row window — but now refining actually
              reaches the rest of the directory. */}
          {results.isSuccess && total > rows.length && (
            <ListboxMessage className="text-caption">
              Showing {rows.length} of {total} matches — keep typing to narrow.
            </ListboxMessage>
          )}

          {/* Last, so it reads as a trailing "…or stop filtering" — the way
              out rather than the first choice. It no longer has to be last
              for correctness: nothing re-homes the cursor behind our back
              now that the listbox is ours. */}
          {clearable && (
            <ListboxOption
              listId={listId}
              value={CLEAR_VALUE}
              active={active === CLEAR_VALUE}
              onSelect={() => choose(null)}
              onHover={() => setActive(CLEAR_VALUE)}
            >
              <Icon
                name="selected"
                className={value === '' ? 'opacity-100' : 'opacity-0'}
              />
              {emptyLabel}
            </ListboxOption>
          )}
        </Listbox>
      </PopoverContent>
    </Popover>
  )
}
